import React, { useEffect, useState, useRef, useMemo } from 'react';
import { connect } from 'react-redux';
import { useRouteMatch, useHistory } from 'react-router-dom';

import VisitsListV2 from './visitsList';
import Visit from '../Visits/visit';
import TextLoader from '../../../components/global/Loader/text';
import Loader from '../../../components/global/Loader';
import EmptyData from '../../../components/global/emptyData';
import Pagination from '../../../components/global/List/Pagination';
import { useUrlParser } from '../../../../utils/useUrlParser';
import { getPatientVisitsSkeleton, getPatientVisitsDetails } from '../../../../api';

const VisitsV2Component = ({ patient, user }) => {
  const { params } = useRouteMatch();
  const history = useHistory();
  const { urlData } = useUrlParser();

  const [skeletonItems, setSkeletonItems] = useState(null);
  const [detailsMap, setDetailsMap] = useState({});
  const [loadingProviders, setLoadingProviders] = useState(new Set());
  const [pagination, setPagination] = useState(null);
  const [skeletonLoading, setSkeletonLoading] = useState(false);
  const [currentPatientId, setCurrentPatientId] = useState(null);
  const [fetchedPage, setFetchedPage] = useState(null);
  const fetchingProviders = useRef(new Set());

  const page = useMemo(() => {
    const p = +urlData.params.page;
    return p > 0 && Number.isInteger(p) ? p : 1;
  }, [urlData.params.page]);

  // Fetch skeleton when patient loads or page changes
  useEffect(() => {
    const patientId = patient.data?.id;

    if (patientId === undefined || patientId === null) return;

    const patientChanged = currentPatientId && currentPatientId !== patientId;
    const needsFetch = skeletonItems === null || patientChanged || fetchedPage !== page;

    if (!needsFetch || skeletonLoading) return;

    setCurrentPatientId(patientId);
    setSkeletonItems(null);
    setDetailsMap({});
    setLoadingProviders(new Set());
    setSkeletonLoading(true);
    setFetchedPage(page);
    fetchingProviders.current = new Set();

    getPatientVisitsSkeleton(patientId, page)
      .then((res) => {
        setSkeletonItems(res.data);
        setPagination(res.pagination);
        setSkeletonLoading(false);
      })
      .catch(() => {
        setSkeletonItems([]);
        setSkeletonLoading(false);
      });
  }, [patient.data, page]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch details per provider group once skeleton is ready
  useEffect(() => {
    if (!skeletonItems?.length || !patient.data?.id) return;

    const patientId = patient.data.id;

    // When viewing a single visit detail, only fetch that visit's provider
    const itemsToFetch = params.id
      ? skeletonItems.filter((item) => item.id === +params.id)
      : skeletonItems;

    const groups = itemsToFetch.reduce((acc, item) => {
      if (!acc[item.provider_id]) acc[item.provider_id] = [];
      acc[item.provider_id].push(item.id);
      return acc;
    }, {});

    const newProviders = Object.entries(groups).filter(
      ([providerId]) => !fetchingProviders.current.has(Number(providerId)),
    );

    if (!newProviders.length) return;

    const newProviderIds = newProviders.map(([id]) => Number(id));
    newProviderIds.forEach((id) => fetchingProviders.current.add(id));

    setLoadingProviders((prev) => {
      const next = new Set(prev);
      newProviderIds.forEach((id) => next.add(id));
      return next;
    });

    newProviders.forEach(([providerId, ids]) => {
      const numericProviderId = Number(providerId);

      getPatientVisitsDetails(patientId, numericProviderId, ids)
        .then((res) => {
          setDetailsMap((prevMap) => {
            const nextMap = { ...prevMap };
            res.data.forEach((visit) => {
              nextMap[visit.id] = visit;
            });
            return nextMap;
          });
        })
        .catch(() => {})
        .finally(() => {
          fetchingProviders.current.delete(numericProviderId);
          setLoadingProviders((prevSet) => {
            const nextSet = new Set(prevSet);
            nextSet.delete(numericProviderId);
            return nextSet;
          });
        });
    });
  }, [skeletonItems]); // eslint-disable-line react-hooks/exhaustive-deps

  // Navigate back if patient changes while on detail view
  useEffect(() => {
    if (params.id && currentPatientId && patient.data?.id !== currentPatientId) {
      history.push('/office/visits');
    }
  }, [currentPatientId, history, params.id, patient.data]);

  const visitDetail = useMemo(() => {
    if (!params.id) return null;
    return detailsMap[+params.id] || null;
  }, [params.id, detailsMap]);

  if (skeletonItems === null || skeletonLoading) {
    return <TextLoader />;
  }

  if (skeletonItems.length === 0 || (params.id && !visitDetail && loadingProviders.size === 0)) {
    return <EmptyData text={params.id ? 'Візит не знайдено' : null} />;
  }

  if (params.id) {
    if (!visitDetail) return <TextLoader />;
    return (
      <div className="personal-visits-wrapper extended">
        <div className="personal-visits">
          <ul className="personal-visits-list">
            <Visit visit={visitDetail} user={user} />
          </ul>
        </div>
      </div>
    );
  }

  return (
    <div className="personal-visits-wrapper extended">
      <div className="personal-visits">
        <ul className="personal-visits-list">
          <VisitsListV2
            skeletonItems={skeletonItems}
            detailsMap={detailsMap}
            loadingProviders={loadingProviders}
            user={user}
          />
        </ul>
      </div>
      <Loader isVisible={loadingProviders.size > 0 || patient.loading} />
      {pagination && (
        <Pagination
          currentPage={pagination.current_page}
          totalPages={pagination.total_pages}
          pageName="page"
        />
      )}
    </div>
  );
};

const mapStateToProps = ({ patient }) => ({ patient });

export default connect(mapStateToProps)(VisitsV2Component);
